import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, RefreshCw, ListOrdered, AlertTriangle, Clock } from 'lucide-react';

const REFRESH_MS = 20_000;
const QUEUE_STATUSES = ['in_production', 'ready', 'revision_requested'];

const STATUS_LABEL = {
  in_production: 'In production',
  ready: 'Ready',
  revision_requested: 'In revision',
};

const STATUS_PILL = {
  in_production: 'bg-amber-50 text-amber-700 border-amber-100',
  ready: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  revision_requested: 'bg-violet-50 text-violet-700 border-violet-100',
};

const SIZE_LABEL = {
  short: 'Short',
  medium: 'Standard',
  large: 'Pro',
  premium: 'Extended',
};

function timeLeft(due, now = Date.now()) {
  if (!due) return null;
  const ms = new Date(due).getTime() - now;
  return ms; // negative = overdue
}

function fmtCountdown(ms) {
  const totalMin = Math.floor(Math.abs(ms) / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d}d ${rh}h`;
  }
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

function timeBadge(due, now) {
  if (!due) {
    return { label: 'No SLA', cls: 'text-gray-400 bg-gray-50 border-gray-100' };
  }
  const ms = timeLeft(due, now);
  if (ms < 0) {
    return {
      label: `OVERDUE · ${fmtCountdown(ms)}`,
      cls: 'text-rose-700 bg-rose-50 border-rose-200',
      overdue: true,
    };
  }
  if (ms < 2 * 60 * 60 * 1000) {
    return { label: `Due in ${fmtCountdown(ms)}`, cls: 'text-amber-700 bg-amber-50 border-amber-200' };
  }
  return { label: `Due in ${fmtCountdown(ms)}`, cls: 'text-emerald-700 bg-emerald-50 border-emerald-100' };
}


export default function ManagerQueue({ onOpenProject }) {
  const { session } = useAuth();
  const { getProject } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState('all'); // all | in_production | ready | revision_requested

  const load = useCallback(async () => {
    if (!session?.access_token) return;
    setError(null);
    const { ok, data } = await xhrJson('GET', `${API}/api/projects`, null, authHeader(session));
    if (ok) {
      const list = (data?.projects || [])
        .filter((p) => QUEUE_STATUSES.includes(p.status))
        .sort((a, b) => {
          const ta = a.delivery_due_at ? new Date(a.delivery_due_at).getTime() : Number.POSITIVE_INFINITY;
          const tb = b.delivery_due_at ? new Date(b.delivery_due_at).getTime() : Number.POSITIVE_INFINITY;
          return ta - tb;
        });
      setRows(list);
    } else {
      setError(data?.detail || "Couldn't load the queue.");
    }
    setLoading(false);
  }, [session?.access_token]);

  // Initial load + interval refresh + 1-min tick for countdowns
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!session?.access_token) return undefined;
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [load, session?.access_token]);

  const filtered = filter === 'all' ? rows : rows.filter((p) => p.status === filter);
  const overdueCount = rows.filter((p) => p.delivery_due_at && new Date(p.delivery_due_at).getTime() < now).length;

  return (
    <div className="flex flex-col h-full" data-testid="manager-queue">
      <div className="px-4 md:px-6 py-4 border-b border-gray-200/60 flex items-center gap-2">
        <ListOrdered className="w-4 h-4 text-gray-400" />
        <h1 className="text-base md:text-lg font-medium text-gray-900 font-['Outfit']">Manager queue</h1>
        <span className="ml-auto flex items-center gap-2 text-[11px]">
          {overdueCount > 0 && (
            <span data-testid="overdue-counter" className="flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">
              <AlertTriangle className="w-3 h-3" />
              {overdueCount} overdue
            </span>
          )}
          <button
            data-testid="queue-refresh"
            onClick={load}
            className="text-[11px] text-gray-400 hover:text-gray-700 transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </span>
      </div>

      {/* Filter chips */}
      <div className="px-4 md:px-6 pt-3 pb-1 flex flex-wrap gap-1.5">
        {[
          { key: 'all', label: `All (${rows.length})` },
          { key: 'in_production', label: `In production (${rows.filter((p) => p.status === 'in_production').length})` },
          { key: 'ready', label: `Ready (${rows.filter((p) => p.status === 'ready').length})` },
          { key: 'revision_requested', label: `In revision (${rows.filter((p) => p.status === 'revision_requested').length})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`queue-filter-${t.key}`}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              filter === t.key
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ScrollArea className="flex-1">
        <div className="px-3 md:px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3 max-w-md">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              <Clock className="w-6 h-6 mx-auto mb-2 text-gray-200" />
              {rows.length === 0
                ? 'No projects in the queue.'
                : 'No projects in this filter.'}
            </div>
          ) : (
            <div className="border border-gray-200/60 rounded-2xl bg-white/40 backdrop-blur-sm overflow-hidden">
              {/* Header (desktop only) */}
              <div className="hidden md:grid grid-cols-[1.6fr_1fr_0.7fr_0.6fr_0.7fr_1.1fr] gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-400 border-b border-gray-100 bg-gray-50/30">
                <span>Project</span>
                <span>Client</span>
                <span>Size</span>
                <span className="text-right tabular-nums">Credits</span>
                <span>Status</span>
                <span className="text-right">Time</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {filtered.map((p, idx) => {
                  const local = getProject(p.id);
                  const clientName = local?.client?.name || p.client_name || p.owner_name || '—';
                  const sizeLabel = SIZE_LABEL[p.project_size] || (p.project_size || '—');
                  const credits = p.credits_consumed ?? 0;
                  const badge = timeBadge(p.delivery_due_at, now);
                  // Queue position (1-based) — derived from sort order since
                  // the list is already sorted by estimated_delivery_at.
                  const queuePos = idx + 1;
                  return (
                    <li key={p.id} data-testid={`queue-row-${p.id}`}>
                      <button
                        onClick={() => onOpenProject?.(p.id)}
                        className="w-full text-left grid grid-cols-2 md:grid-cols-[1.6fr_1fr_0.7fr_0.6fr_0.7fr_1.1fr] gap-2 md:gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <span className="col-span-2 md:col-span-1 text-sm text-gray-900 truncate pr-2 flex items-center gap-2">
                          <span
                            data-testid={`queue-pos-${p.id}`}
                            className="shrink-0 w-5 h-5 rounded-full bg-gray-900 text-white text-[10px] font-medium flex items-center justify-center tabular-nums"
                            title={`Queue position`}
                          >
                            {queuePos}
                          </span>
                          <span className="truncate">{p.name}</span>
                        </span>
                        <span className="text-[12px] text-gray-500 truncate">
                          <span className="md:hidden text-[10px] uppercase tracking-[0.15em] text-gray-300 mr-1">Client</span>
                          {clientName}
                        </span>
                        <span className="text-[12px] text-gray-600 truncate">{sizeLabel}</span>
                        <span className="text-[12px] text-gray-600 tabular-nums md:text-right">
                          <span className="md:hidden text-[10px] uppercase tracking-[0.15em] text-gray-300 mr-1">Credits</span>
                          {credits}
                        </span>
                        <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border w-fit ${STATUS_PILL[p.status] || ''}`}>
                          {STATUS_LABEL[p.status] || p.status}
                        </span>
                        <span
                          data-testid={`queue-time-${p.id}`}
                          className={`inline-flex items-center md:justify-end text-[11px] px-2 py-0.5 rounded-full border w-fit md:ml-auto tabular-nums ${badge.cls}`}
                        >
                          {badge.overdue && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {badge.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-gray-400 mt-3 px-1">
            Auto-refresh every {REFRESH_MS / 1000}s · Sorted by SLA (most urgent on top).
          </p>
        </div>
      </ScrollArea>
    </div>
  );
}
